import axios, { headersTags } from '@/libs/axios'
import shipments from '@/apis/shipments'
import collect from 'collect.js'
import moment from '@/libs/moment'
import expeditionSheets from '@/apis/expedition_sheets'
import deliveries from '@/apis/deliveries'

/**
 @typedef ExpeditionSheetDays
 @property {String} date
 @property {Array<Stats>} stats
 @property {Array<ExpeditionSheet>} sheets
 @property {Array<Delivery>} deliveries
 */

/**
 @typedef {Object} ExpeditionSheet
 @property {Number} id
 @property {string} uuid
 @property {string} datetime
 @property {Number} plot_id
 @property {Number} crop_id
 @property {Number} crop_type_id
 @property {Number} crop_grade_id
 @property {Number} crop_packing_id
 @property {Number} document_template_id
 @property {Number} quantity
 @property {string} state
 @property {string} send_at
 @property {string} created_at
 @property {string} updated_at
 @property {string} deleted_at
 */

/**
 * @typedef {Object} Family
 * @property {string} slug - Slug de la famille.
 */

/**
 * @typedef {Object} Crop
 * @property {string} slug - Slug de la culture.
 * @property {string} name - Nom de la culture.
 * @property {string} emoji - Emoji représentant la culture.
 * @property {string} color - Couleur associée à la culture.
 * @property {Family} family - Famille de la culture.
 */

/**
 * @typedef {Object} Type
 * @property {string} id - Identifiant du type.
 * @property {string} name - Nom du type.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total.
 * @property {number} quantity - Quantité totale.
 */

/**
 * @typedef {Object} Packing
 * @property {string} id - Identifiant du conditionnement.
 * @property {string} name - Nom du conditionnement.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total des conditionnements.
 * @property {number} quantity - Quantité totale des conditionnements.
 */

/**
 * @typedef {Object} Plot
 * @property {string} id - Identifiant de la parcelle.
 * @property {string} name - Nom de la parcelle.
 * @property {string} abbreviation - Abréviation de la parcelle.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total de la parcelle.
 * @property {number} quantity - Quantité totale de la parcelle.
 */

/**
 * @typedef {Object} Account
 * @property {string} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 * @property {number[]} weight - Liste des poids individuels gérés par ce compte.
 * @property {number[]} quantities - Liste des quantités individuelles gérées par ce compte.
 * @property {number} total_weight - Poids total associé au compte.
 * @property {number} quantity - Quantité totale associée au compte.
 */

/**
 * @typedef {Object} Stats
 * @property {Crop} crop - Détails de la culture.
 * @property {Object<string, Type>} types - Liste des types indexés par identifiants.
 * @property {Object<string, Packing>} packings - Liste des conditionnements indexés par identifiants.
 * @property {Object<string, Plot>} plots - Liste des parcelles indexées par identifiants.
 * @property {string[]} options - Liste des options disponibles.
 * @property {Object<string, Account>} accounts - Liste des comptes indexés par identifiants.
 * @property {Array} grades - Liste des grades (actuellement vide).
 * @property {number} total_weight - Poids total global.
 * @property {number} quantity - Quantité totale globale.
 */

/**
 * @typedef {Object} Account
 * @property {number} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 */

/**
 * @typedef {Object} Address
 * @property {number} id - Identifiant de l'adresse.
 * @property {string} complete_address - Adresse complète.
 * @property {Account} account - Compte associé à l'adresse.
 */

/**
 * @typedef {Object} Farmer
 * @property {string} name - Nom de l'agriculteur.
 */

/**
 * @typedef {Object} Carrier
 * @property {number} id - Identifiant du transporteur.
 * @property {string} name - Nom du transporteur.
 */

/**
 * @typedef {Object} ExpeditionSheet
 * @property {number} id - Identifiant de la feuille d'expédition.
 * @property {string} datetime - Date et heure de l'expédition (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État de la feuille d'expédition.
 * @property {string} external_id - Identifiant externe de la feuille d'expédition.
 * @property {string} pdf_url - URL du PDF associé.
 * @property {boolean} is_valid - Indique si la feuille est valide.
 * @property {Farmer} farmer - Agriculteur associé.
 * @property {Account} account - Compte associé à l'expédition.
 * @property {Carrier|null} carrier - Transporteur associé (nullable).
 */

/**
 * @typedef {Object} DeliverySheet
 * @property {string} external_id - Identifiant externe de la livraison.
 * @property {string} json_url - URL du fichier JSON associé.
 * @property {string} xml_url - URL du fichier XML associé.
 * @property {string} pdf_url - URL du fichier PDF associé.
 * @property {Farmer} farmer - Agriculteur associé à cette livraison.
 */

/**
 * @typedef {Object} Delivery
 * @property {string} datetime - Date et heure principales (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État principal de l'objet.
 * @property {string} external_id - Identifiant externe principal.
 * @property {boolean} is_valid - Indique si l'objet principal est valide.
 * @property {Farmer[]} farmers - Liste des agriculteurs associés.
 * @property {ExpeditionSheet[]} expedition_sheets - Liste des feuilles d'expédition associées.
 * @property {DeliverySheet[]} delivery_sheets - Liste des feuilles de livraison associées.
 * @property {Account} account - Compte principal associé.
 * @property {Address} account_shipping_address - Adresse de livraison associée au compte.
 * @property {Carrier} carrier - Transporteur principal.
 */

export default {
  namespaced: true,
  state: {
    /** @type {null|Array<ExpeditionSheetDays>} list */
    list: null,
    /** @type {string|null} lastPull */
    lastPull: null,
    /** @type {Number} currentPage */
    currentPage: 0,
    /** @type {Number} total */
    total: 0,
  },
  getters: {
    /**
     * @param state
     * @returns {boolean}
     */
    isFetch(state) {
      return state.list !== null
        && state.lastPull !== null
        && state.currentPage !== 0
    },
    /**
     * @param state
     * @returns {boolean}
     */
    hasOne(state) {
      return state.list !== null && state.list.filter(e => (e.display === true)).length > 0
    },
    /**
     * @param state
     * @returns {boolean}
     */
    hasAll(state) {
      return state.list !== null && state.list.filter(e => (e.display === true)).length === state.total
    },
    /**
     * @param state
     * @returns {number}
     */
    count(state) {
      return state.list !== null ? state.list.filter(e => (e.display === true)).length : 0
    },
    /**
     * @param state
     * @returns {boolean}
     */
    has(state) {
      return state.list !== null && state.list.filter(e => (e.display === true)).length >= 1
    },
    /**
     * @param state
     * @returns {Array<ExpeditionSheetDays>}
     */
    getList(state) {
      return state.list.filter(e => (e.display === true && moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') !== moment()
        .locale('en')
        .format('YYYY-MM-DD')))
    },
    /**
     * @param state
     * @returns {moment|null}
     */
    getLastPull(state) {
      return moment(state.lastPull)
    },
    /**
     * @param state
     * @returns {Array<ExpeditionSheetDays>}
     */
    getListOrderByMonth(state) {
      const ordered = {}

      state.list
        .filter(e => (e.display === true && moment(e.date)
          .locale('en')
          .format('YYYY-MM-DD') !== moment()
          .locale('en')
          .format('YYYY-MM-DD')))
        .forEach(e => {
          const month = `${e.date.split('-')[0]}-${e.date.split('-')[1]}`
          if (typeof ordered[month] === 'undefined') {
            ordered[month] = []
          }

          ordered[month].push(e)
        })

      return ordered
    },
  },
  mutations: {
    clear(state) {
      state.list = null
      state.lastPull = null
      state.currentPage = 0
    },
    /**
     * @param state
     * @param {Array<Farmer>} list
     * @param {Number} currentPage
     * @param {Number} total
     * @returns {Promise<void>}
     */
    set(state, {
      list,
      currentPage,
      total,
    }) {
      // eslint-disable-next-line no-param-reassign
      list = list.map(e => {
        e.display = true
        return e
      })

      state.list = (state.list === null ? [...list] : [...state.list.filter(e => !(list.map(a => a.date)
        .includes(e.date))), ...list])
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD')
      state.total = total
      state.currentPage = currentPage
    },
    /**
     * @param state
     * @param {String} date
     * @param {array<ExpeditionSheet>} sheets
     */
    pullSheets(state, {
      date,
      data,
    }) {
      const element = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0]
      if (typeof element === 'undefined') return

      state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0].sheets = data
    },
    /**
     * @param state
     * @param {String} date
     * @param {array<Delivery>} data
     */
    pullDeliveries(state, {
      date,
      data,
    }) {
      const element = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0]
      if (typeof element === 'undefined') return

      state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0].deliveries = data
    },
    /**
     * @param state
     * @param {String} date
     * @param {array<ExpeditionSheet>} sheets
     */
    pullStats(state, {
      date,
      data,
    }) {
      const element = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0]
      if (typeof element === 'undefined') return

      state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0].stats = data
    },
    /**
     * @param state
     * @param {String} date
     */
    addManualDate(state, { date }) {
      const element = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))[0]
      if (typeof element === 'undefined') {
        state.list = [...state.list, {
          date,
          display: false,
        }]
      }
    },
  },
  actions: {
    /**
     * @param commit
     * @param state
     * @param getters
     * @param rootGetters
     * @param {boolean} clearBefore
     * @returns {Promise<boolean>}
     */
    async fetch({
      commit,
      state,
      getters,
      rootGetters,
    }, clearBefore = false) {
      /** @type {AxiosResponse<loginResponse>} response */
      const response = await (axios(shipments.calendar(
        rootGetters['auth/getToken'],
        rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
        {
          page: clearBefore ? 1 : state.currentPage + 1,
        },
        {
          [headersTags.ORDER_BY]: JSON.stringify({
            end: 'desc',
            start: 'desc',
            name: 'asc',
          }),
        },
      )))

      if (clearBefore) commit('clear')
      commit('set', {
        list: response.data?.data,
        currentPage: response.data?.last_page <= response.data?.current_page ? response.data?.last_page : response.data?.current_page,
        total: response.data?.total,
      })

      return (getters.hasAll)
    },
    async loadStats({
      rootGetters,
      state,
      commit,
    }, {
      date,
      force = false,
      byPassStorageCaching = false,
    }) {
      const find = state.list === null ? null : state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (
        !force
        && find.length !== 0
        && typeof find[0].stats !== 'undefined'
        && find[0].stats !== null
      ) {
        return
      }

      const response = await (axios(await (shipments.stats(
        rootGetters['auth/getToken'],
        rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
        moment(date)
          .locale('en')
          .format('YYYY-MM-DD'),
      ))))

      if (byPassStorageCaching) {
        commit('addManualDate', {
          date,
        })
      }

      commit('pullStats', {
        date,
        data: response.data,
      })
    },
    /**
     * @param state
     * @param rootGetters
     * @param dispatch
     * @param {String} date
     * @param {boolean} force
     * @param {boolean} byPassStorageCaching
     *
     * @return {Boolean|Object}
     */
    async load({
      rootGetters,
      dispatch,
    }, {
      date,
      force = false,
      byPassStorageCaching = false,
    }) {
      const requests = [
        dispatch('loadStats', {
          date,
          force,
          byPassStorageCaching,
        }),
      ]

      if (rootGetters['modules/getModules'].includes('shipping')) {
        requests.push(
          dispatch('loadSheets', {
            date,
            force,
            byPassStorageCaching,
          }),
        )
      }

      if (rootGetters['modules/getModules'].includes('shipping-delivery')) {
        requests.push(
          dispatch('loadDeliveries', {
            date,
            force,
            byPassStorageCaching,
          }),
        )
      }

      await Promise.all(requests)
    },
    async loadSheets({
      state,
      rootGetters,
      commit,
    }, {
      date,
      force = false,
      byPassStorageCaching = false,
    }) {
      const find = state.list === null ? null : state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (
        !force
        && find.length !== 0
        && typeof find[0].sheets !== 'undefined'
        && find[0].sheets !== null
      ) {
        return true
      }

      const fields = [
        'farmer.id',
        'farmer.name',
        'external_id',
        'crop_family.id',
        'crop_family.slug',
        'crop.slug',
        'crop.name',
        'crop.emoji',
        'crop.color',
        'crop_type.slug',
        'crop_type.name',
        'crop_type.options',
        'crop_grade.slug',
        'crop_grade.name',
        'crop_grade.weight',
        'crop_packing.slug',
        'crop_packing.name',
        'crop_packing.weight',
        'account.id',
        'account.name',
        'quantity',
        'datetime',
        'plot.slug',
        'plot.name',
        'plot.abbreviation',
        'carrier.id',
        'carrier.name',
        'quantity',
        'state',
        'send_at',
        'is_valid',
        'pdf_url',
        'xml_url',
        'json_url',
      ]
      const response = await (axios(expeditionSheets.list(
        rootGetters['auth/getToken'],
        rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
        moment(date)
          .locale('en')
          .format('YYYY-MM-DD'),
        {},
        {
          [headersTags.ORDER_BY]: JSON.stringify({
            datetime: 'desc',
          }),
          [headersTags.FIELDS]: JSON.stringify(fields),
          [headersTags.NO_PAGINATE]: true,
        },
      )))

      if (byPassStorageCaching) {
        commit('addManualDate', {
          date,
        })
      }

      commit('pullSheets', {
        date,
        data: response.data?.data,
      })

      return response.data?.data.length >= 1
    },
    async loadDeliveries({
      state,
      rootGetters,
      commit,
    }, {
      date,
      force = false,
      byPassStorageCaching = false,
    }) {
      const find = state.list === null ? null : state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (
        !force
        && find.length !== 0
        && typeof find[0].deliveries !== 'undefined'
        && find[0].deliveries !== null
      ) {
        return true
      }

      const relations = [
        'delivery.farmers',
        'delivery.account_shipping_address',
        'delivery.expedition_sheets',
        'delivery.delivery_sheets',
      ]
      const fields = [
        'farmers',
        'farmer.name',
        'farmer.name',
        'external_id',
        'account_shipping_address',
        'account_address.id',
        'account_address.complete_address',
        'account.id',
        'account.name',
        'datetime',
        'carrier.id',
        'carrier.name',
        'expedition_sheets',
        'crop.slug',
        'expedition_sheet.id',
        'expedition_sheet.external_id',
        'expedition_sheet.pdf_url',
        'state',
        'is_valid',
        'delivery_sheets',
        'delivery_sheet.pdf_url',
        'delivery_sheet.xml_url',
        'delivery_sheet.json_url',
      ]
      const response = await (axios(deliveries.list(
        rootGetters['auth/getToken'],
        rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
        moment(date)
          .locale('en')
          .format('YYYY-MM-DD'),
        {},
        {
          [headersTags.ORDER_BY]: JSON.stringify({
            datetime: 'desc',
          }),
          [headersTags.NO_PAGINATE]: true,
          [headersTags.FIELDS]: JSON.stringify(fields),
          [headersTags.WITH_RELATIONS]: JSON.stringify(relations),
        },
      )))

      if (byPassStorageCaching) {
        commit('addManualDate', {
          date,
        })
      }

      commit('pullDeliveries', {
        date,
        data: response.data?.data,
      })

      return response.data?.data.length >= 1
    },
    /**
     * @param state
     * @param {String} date
     */
    has({
      state,
    }, date) {
      if (state.list === null) return null

      const find = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      return (find.length >= 1)
    },
    /**
     * @param state
     * @param {String} date
     */
    find({
      state,
    }, date) {
      if (state.list === null) return null

      const find = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (find.length >= 1) return find[0]

      return null
    },
    /**
     * @param state
     * @param {String} date
     * @param {null|Number} crop
     * @returns {Array<Stats>}
     */
    getStats({
      state,
    }, {
      date,
      crop = null,
    }) {
      if (state.list === null) return null
      const find = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (find.length !== 1) return null

      let { stats } = find[0]

      if (crop !== null) {
        stats = collect(find[0].stats)
          .filter(e => e.crop.slug.toString() === crop.toString())
          .all()
      }

      return stats
    },
    /**
     * @param state
     * @param {String} date
     * @param {null|Number|String} crop
     * @returns {Array<ExpeditionSheet>}
     */
    getSheets({
      state,
    }, {
      date,
      crop = null,
    }) {
      if (state.list === null) return null

      const find = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (find.length !== 1) return null

      let query = find[0].sheets

      if (crop !== null) {
        query = query.filter(e => e.crop.slug.toString() === crop.toString())
      }

      return query
    },
    getDeliveries({
      state,
    }, {
      date,
      crop = null,
    }) {
      if (state.list === null) return null

      const find = state.list.filter(e => moment(e.date)
        .locale('en')
        .format('YYYY-MM-DD') === moment(date)
        .locale('en')
        .format('YYYY-MM-DD'))
      if (find.length !== 1) return null

      let query = find[0].deliveries

      if (crop !== null) {
        query = query
          .filter(e => e.expedition_sheets
            .filter(sheet => sheet.crop.slug.toString() === crop.toString()).length > 0)
      }

      return query
    },
  },
}
